/**
 * The authentication state machine, in one place.
 *
 * Every screen in this package reads from here rather than calling the API
 * itself, because sign-in is not one request — it is a sequence with a branch
 * (a second factor may or may not be demanded) and a shared piece of state
 * (the challenge id, which *is* a credential). Spread across screens, that
 * becomes several partial copies of the same flow, and the one that forgets to
 * clear the challenge on failure is a security bug rather than a display bug.
 *
 *     restoring ──► anonymous ──sign in──► authenticated
 *                       ▲                       ▲
 *                       │                  verify code
 *                       └── cancel ── mfa_required
 *
 * `restoring` exists because the access token lives in memory only: a page
 * reload always starts with no token and a refresh cookie the page cannot
 * read. Until that exchange resolves, the answer to "is anyone signed in" is
 * genuinely unknown, and the router's bootstrap gate holds navigation rather
 * than letting a private screen render for a frame.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { NetworkError, type ApiClient, type User } from "@hardauto/core-api";

import {
  SessionContext,
  type MfaChallenge,
  type SessionContextValue,
  type SessionStatus,
} from "./session";

/** The server's answer to a sign-in that needs a second factor. */
interface MfaRequiredPayload {
  status: string;
  challenge_id: string;
  method_id: string;
  channel?: string | null;
  available_methods?: { enrollment_id: string; method_id: string }[];
}

interface SessionPayload {
  access_token: string;
  access_token_expires_at: string;
  lineage_id: string;
}

type LoginResponse = SessionPayload | MfaRequiredPayload;

function isMfaRequired(payload: LoginResponse): payload is MfaRequiredPayload {
  return "challenge_id" in payload;
}

export interface SessionProviderProps {
  client: ApiClient;
  children: ReactNode;
  /** Device name sent with a sign-in, so the session list is legible. */
  deviceName?: string;
}

export function SessionProvider({ client, children, deviceName }: SessionProviderProps) {
  const [status, setStatus] = useState<SessionStatus>("restoring");
  const [user, setUser] = useState<User | null>(null);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);

  // Guards against setting state on an unmounted tree, which the restore
  // effect can easily do: it awaits two round trips.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadUser = useCallback(async (): Promise<User | null> => {
    const me = await client.request<User>(client.authenticatedPath("/me"));
    if (mounted.current) setUser(me);
    return me;
  }, [client]);

  /**
   * Restore the session on first mount.
   *
   * A failure here is NOT an error to show. The overwhelmingly common cause is
   * "nobody is signed in", which is a perfectly ordinary state for someone
   * opening the sign-in page. The one case worth separating is a network
   * failure: the user may well have a valid session and simply be offline, so
   * clearing it would sign them out for a dropped connection.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await loadUser();
        if (!cancelled && mounted.current) setStatus("authenticated");
      } catch (error) {
        if (cancelled || !mounted.current) return;
        if (error instanceof NetworkError) {
          // Offline. Treat as anonymous for routing — there is nothing else
          // the interface can do — but do not clear anything.
          setStatus("anonymous");
          return;
        }
        setStatus("anonymous");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  const toChallenge = useCallback((payload: MfaRequiredPayload): MfaChallenge => {
    return {
      challengeId: payload.challenge_id,
      method: payload.method_id,
      sentTo: payload.channel ?? undefined,
      available: (payload.available_methods ?? []).map((method) => ({
        id: method.enrollment_id,
        method: method.method_id,
        // The label is resolved from the method id at render, so this package
        // ships no user-facing English.
        label: method.method_id,
      })),
    };
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SessionStatus> => {
      const payload = await client.request<LoginResponse>(client.anonymousPath("/login"), {
        method: "POST",
        anonymous: true,
        body: {
          email,
          password,
          ...(deviceName ? { device_name: deviceName } : {}),
        },
      });

      if (isMfaRequired(payload)) {
        setChallenge(toChallenge(payload));
        setStatus("mfa_required");
        return "mfa_required";
      }

      client.adoptSession(payload);
      await loadUser();
      setChallenge(null);
      setStatus("authenticated");
      return "authenticated";
    },
    [client, deviceName, loadUser, toChallenge],
  );

  const startSocial = useCallback(
    async (provider: string, redirectTo?: string): Promise<string> => {
      const payload = await client.request<{ authorization_url: string }>(
        client.anonymousPath(`/oauth/${encodeURIComponent(provider)}/start`),
        {
          method: "POST",
          anonymous: true,
          body: redirectTo ? { redirect_to: redirectTo } : {},
        },
      );
      // The state and the PKCE verifier stay on the server. Nothing is stored
      // here, which is what makes this safe to start in one tab and finish in
      // another — and why there is nothing to clean up if the user abandons it.
      return payload.authorization_url;
    },
    [client],
  );

  const completeSocial = useCallback(
    async (code: string, state: string): Promise<SessionStatus> => {
      const payload = await client.request<LoginResponse>(client.anonymousPath("/oauth/callback"), {
        method: "POST",
        anonymous: true,
        body: {
          code,
          state,
          ...(deviceName ? { device_name: deviceName } : {}),
        },
      });

      // Exactly the branches `signIn` has. Duplicated deliberately rather
      // than factored out: the two differ in what they send, and a shared
      // helper taking a body would hide which endpoint a failure came from.
      if (isMfaRequired(payload)) {
        setChallenge(toChallenge(payload));
        setStatus("mfa_required");
        return "mfa_required";
      }

      client.adoptSession(payload);
      await loadUser();
      setChallenge(null);
      setStatus("authenticated");
      return "authenticated";
    },
    [client, deviceName, loadUser, toChallenge],
  );

  const verifyMfa = useCallback(
    async (code: string): Promise<SessionStatus> => {
      if (!challenge) throw new Error("There is no challenge to verify.");

      /**
       * The challenge is kept, whatever goes wrong — there is no catch here
       * on purpose.
       *
       * The obvious implementation clears it on `mfa_challenge_invalid`,
       * reasoning that a spent challenge cannot be retried. That does not work
       * here, and the reason is a deliberate server decision: an MFA code is
       * short enough to guess, so the API returns ONE error for every failure
       * — a wrong code, an expired challenge and an unknown id are
       * indistinguishable, precisely so a caller cannot confirm a live
       * challenge id.
       *
       * Given that, the client has to choose. Clearing sent the user back to
       * the password form for a single mistyped digit, which is the common
       * case by a wide margin; keeping it leaves someone with a genuinely dead
       * challenge on a form that will not accept anything — and "Back to sign
       * in" is on screen. The server's own attempt counter is what actually
       * ends a challenge, and it does not need the client's help.
       */
      const payload = await client.request<SessionPayload>(
        client.authenticatedPath("/mfa/verify"),
        {
          method: "POST",
          anonymous: true,
          body: { challenge_id: challenge.challengeId, code },
        },
      );
      client.adoptSession(payload);
      await loadUser();
      setChallenge(null);
      setStatus("authenticated");
      return "authenticated";
    },
    [challenge, client, loadUser],
  );

  const switchMfa = useCallback(
    async (enrollmentId: string): Promise<void> => {
      if (!challenge) throw new Error("There is no challenge to switch.");
      const payload = await client.request<MfaRequiredPayload>(
        client.authenticatedPath("/mfa/challenge/switch"),
        {
          method: "POST",
          anonymous: true,
          body: { challenge_id: challenge.challengeId, enrollment_id: enrollmentId },
        },
      );
      // The switch issues a NEW challenge id; keeping the old one would send
      // the next code against a challenge the server has already retired.
      setChallenge(toChallenge(payload));
    },
    [challenge, client, toChallenge],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await client.request(client.authenticatedPath("/logout"), { method: "POST", body: {} });
    } catch (error) {
      // Swallowed, and NOT re-thrown. Signing out is one of the few actions
      // where the local outcome is the one the user asked for: they will be
      // signed out here whatever the server says, so making every call site
      // handle a rejection would only produce error banners on a screen the
      // user is leaving. Logged, because a logout that never reaches the
      // server leaves a live session behind and that is worth knowing.
      console.warn("Sign-out did not reach the server; the session was cleared locally.", error);
    } finally {
      // In `finally`, deliberately. If the request fails — offline, or a token
      // that has already expired — the user pressed "sign out" and must end up
      // signed out locally regardless. Leaving them apparently signed in
      // because the server could not be reached is the worse outcome by far,
      // especially on a shared machine.
      client.clearSession();
      if (mounted.current) {
        setUser(null);
        setChallenge(null);
        setStatus("anonymous");
      }
    }
  }, [client]);

  const refreshUser = useCallback(async (): Promise<void> => {
    await loadUser();
  }, [loadUser]);

  const cancelMfa = useCallback((): void => {
    setChallenge(null);
    setStatus("anonymous");
  }, []);

  /**
   * React to a session lost elsewhere — a refresh that failed, a 401 on any
   * request, an administrator revoking the session from another device.
   *
   * Subscribing rather than polling means the interface reacts within the same
   * tick as the client discovering it, so the user is redirected instead of
   * watching a screen fill with permission errors.
   */
  useEffect(
    () =>
      client.onSessionLost(() => {
        if (!mounted.current) return;
        setUser(null);
        setChallenge(null);
        setStatus("anonymous");
      }),
    [client],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user,
      challenge,
      signIn,
      startSocial,
      completeSocial,
      verifyMfa,
      switchMfa,
      signOut,
      refreshUser,
      cancelMfa,
    }),
    [
      status,
      user,
      challenge,
      signIn,
      startSocial,
      completeSocial,
      verifyMfa,
      switchMfa,
      signOut,
      refreshUser,
      cancelMfa,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
